import { differenceInDays, format, parseISO } from 'date-fns';
import { saveAs } from 'file-saver';
import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

import { getUser } from '../modules/contexts/Auth/RequireAuth';
import { IDominioSelect } from '../modules/types/Dominio';
import { EnumOption } from '../modules/types/types';
import { logExportacaoDados } from '../services/logs/logsAcoesService';

const user = getUser();
const userID = user.sub;

export function formataUserName(fullName: string): string {
  const parts = fullName.split(' ');
  const firstName: string = parts[0];
  const lastName: string = parts[parts.length - 1];

  return firstName + ' ' + lastName;
}

export function formataNomeMeioSiglas(fullName: string): string {
  const parts = fullName.split(' ');
  const firstName: string = parts[0];
  const lastName: string = parts[parts.length - 1];
  const middleNamesInitials: string = parts
    .slice(1, parts.length - 1)
    .filter((name) => name.length > 2) // Filtra nomes do meio com mais de 2 letras
    .map((name) => name.charAt(0).toUpperCase())
    .join('');

  return `${firstName} ${middleNamesInitials} ${lastName}`;
}

export function formataCPF(raw?: string | number | null): string {
  if (raw === null || raw === undefined) return '';

  const digits = String(raw).replace(/\D/g, '').slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;

  // 10 ou 11 dígitos
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

// Função para validar formato de data recebido
function isValidDate(dateString: string): boolean {
  if (!dateString || dateString.trim() === '') return false;
  return !isNaN(Date.parse(dateString));
}

// Funções para formatar a data
export function formatDate(dateString: string): string {
  if (!isValidDate(dateString)) {
    return '-';
  }
  const date = parseISO(dateString);
  return format(new Date(date), 'dd/MM/yyyy HH:mm:ss');
}

export function formatDateForInput(dateString: string): string {
  if (!dateString) return '';
  return parseISO(dateString).toISOString().split('T')[0]; // "YYYY-MM-DD"
}

export function formatDateIntl(dateString: string): string {
  if (!isValidDate(dateString)) {
    return '';
  }
  const date = parseISO(dateString);
  return format(new Date(date), 'yyyy-MM-dd');
}

// Função para formatar a data
export function formatDateSimp(dateString: string): string {
  if (!isValidDate(dateString)) {
    return '-';
  }
  const date = parseISO(dateString);
  return format(new Date(date), 'dd/MM/yyyy');
}

export function newLocalDate() {
  return format(new Date(), 'yyyy-MM-dd');
}

// Função para calcular a diferença entre duas datas em dias
export function calcularDiferencaEmDias(
  dataInicio: string,
  dataFim: string,
): number {
  if (!dataInicio) {
    throw new Error('A data de início deve ser fornecida');
  }

  // Converter strings para objetos Date usando parseISO
  const dataInicioParsed = parseISO(dataInicio.split('/').reverse().join('-'));
  const dataFimParsed =
    dataFim === '-'
      ? new Date()
      : parseISO(dataFim.split('/').reverse().join('-'));

  // Calcular a diferença em dias
  const diferencaDias = differenceInDays(dataFimParsed, dataInicioParsed);

  return diferencaDias;
}

export function formatDateToFile(dateString: string): string {
  if (!isValidDate(dateString)) {
    return '';
  }
  const date = parseISO(dateString);
  return format(new Date(date), 'yyyyMMddHHmmss');
}

export async function ExportToExcel(
  data: any[],
  filtroExibicao: Record<string, string>,
  fields: string[],
  filename: string,
  userId: number, // Novo parâmetro
  interfaceSisAf: string, // Novo parâmetro
  entidade: string, // Novo parâmetro
  arrayTituloPlanilha?: string[], // Novo parâmetro usado para definir nomes das planilhas
) {
  const workbook = XLSX.utils.book_new();

  if (Array.isArray(data[0])) {
    // Caso `data` contenha múltiplos arrays internos
    data.forEach((subArray, index) => {
      const modifiedData = desacoplaObject(subArray, fields);

      const transformedData = modifiedData.map((item) =>
        transformData(item, filtroExibicao),
      );

      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      if (!arrayTituloPlanilha)
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          `Planilha${index + 1}`,
        );
      else
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          arrayTituloPlanilha[index],
        );
    });
  } else {
    // Caso `data` seja um único array
    const modifiedData = desacoplaObject(data, fields);

    const transformedData = modifiedData.map((item) =>
      transformData(item, filtroExibicao),
    );

    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  }

  // Gerar o arquivo Excel
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  saveAs(blob, filename);

  // Registrar a exportação de dados no backend
  await logExportacaoDados(parseInt(userId.toString(), 10), entidade);
}

export const fieldObjects = [
  'usuarioRegistro.nomeCompleto',
  'usuarioAtualizacao.nomeCompleto',
  'usuarioUltimaVisualizacao.nomeCompleto',
];
export async function ExportToExcel2(
  data: any[],
  filtroExibicao: Record<string, string>,

  filename: string,
  interfaceSisAf: string, // Novo parâmetro
  entidade: string, // Novo parâmetro
  arrayTituloPlanilha?: string[], // Novo parâmetro usado para definir nomes das planilhas
) {
  const workbook = XLSX.utils.book_new();

  if (Array.isArray(data[0])) {
    // Caso `data` contenha múltiplos arrays internos
    data.forEach((subArray, index) => {
      const modifiedData = desacoplaObject(subArray, fieldObjects);

      const transformedData = modifiedData.map((item) =>
        transformData(item, filtroExibicao),
      );

      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      if (!arrayTituloPlanilha)
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          `Planilha${index + 1}`,
        );
      else
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          arrayTituloPlanilha[index],
        );
    });
  } else {
    // Caso `data` seja um único array
    const modifiedData = desacoplaObject(data, fieldObjects);

    const transformedData = modifiedData.map((item) =>
      transformData(item, filtroExibicao),
    );

    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  }

  // Gerar o arquivo Excel
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  saveAs(blob, filename);

  // Registrar a exportação de dados no backend
  await logExportacaoDados(parseInt(userID.toString(), 10), entidade);
}
function transformData(
  item: Record<string, any>,
  filtroExibicao: Record<string, string>,
) {
  const transformedItem: Record<string, any> = {};
  for (const key in item) {
    const newKey = filtroExibicao[key] || key;
    if (key === 'stRegistroAtivo' || key.includes('Array')) {
      continue;
    } else if (
      (key.startsWith('data') || key.startsWith('DATA')) &&
      item[key]
    ) {
      transformedItem[newKey] = formatDate(item[key]);
    } else {
      transformedItem[newKey] = item[key];
    }
  }
  console.log(transformedItem);
  return transformedItem;
}

export function desacoplaObject(
  data: any[] | undefined,
  fields: string[] | undefined,
) {
  if (!data || !fields) {
    return [];
  }

  return data.map((item) => {
    const modifiedData = { ...item };

    fields.forEach((field) => {
      const keys = field.split('.');
      const mainKey = keys[0];
      const subKey = keys[1];
      if (modifiedData[mainKey] && modifiedData[mainKey][subKey]) {
        modifiedData[mainKey] = modifiedData[mainKey][subKey];
      }
    });

    return modifiedData;
  });
}

export function useCalculaPercentualDisponivel(...valores: string[]): string {
  const [percentualDisponivel, setPercentualDisponivel] = useState('100%');

  useEffect(() => {
    function calculaPercentual() {
      let totalPixels = 0;
      let totalPercent = 0;

      valores.forEach((valor) => {
        if (valor.includes('px')) {
          const pixels = parseFloat(valor.replace('px', ''));
          totalPixels += pixels;
        } else if (valor.includes('%')) {
          const percent = parseFloat(valor.replace('%', ''));
          totalPercent += percent;
        }
      });

      const larguraViewport = window.innerWidth;
      const percentualPixels = (totalPixels / larguraViewport) * 100;
      const percentualDisponivel = 98 - totalPercent - percentualPixels;

      setPercentualDisponivel(
        percentualDisponivel > 0 ? `${percentualDisponivel}%` : '0%',
      );
    }

    // Calcula o percentual inicial
    calculaPercentual();

    // Adiciona um listener para o evento de redimensionamento da janela
    window.addEventListener('resize', calculaPercentual);

    // Remove o listener quando o componente é desmontado
    return () => {
      window.removeEventListener('resize', calculaPercentual);
    };
  }, [valores]);

  return percentualDisponivel;
}

// Função para formatar CNPJ no formato XX.XXX.XXX/XXXX-XX
export const formatarCnpj = (cnpj: string) => {
  cnpj = cnpj.replace(/\D/g, ''); // Remove caracteres não numéricos
  if (cnpj.length > 14) cnpj = cnpj.slice(0, 14); // Limita o tamanho a 14 dígitos
  return cnpj
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})/, '$1-$2');
};

// Função para validar CNPJ
export const validarCnpj = (cnpj: string) => {
  cnpj = cnpj.replace(/\D/g, ''); // Remove caracteres não numéricos

  if (cnpj.length !== 14) return false;

  // Cálculo dos dígitos verificadores
  const verificarDigito = (cnpj: string, posicao: number) => {
    const pesos =
      posicao === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = cnpj
      .substring(0, posicao)
      .split('')
      .reduce((acc, curr, index) => acc + parseInt(curr) * pesos[index], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const digito1 = verificarDigito(cnpj, 12);
  const digito2 = verificarDigito(cnpj, 13);

  return (
    digito1 === parseInt(cnpj.charAt(12)) &&
    digito2 === parseInt(cnpj.charAt(13))
  );
};

export const checkValueStatus = (value: string): number => {
  return value === 'S' ? 1 : 2;
};

export const formatarNumero = (value: string | number) => {
  let stringValue;
  // Remove todos os caracteres não numéricos e formata com separadores de milhar
  if (!value) {
    return '';
  } else if (typeof value !== 'string') {
    stringValue = value.toString();
  } else {
    stringValue = value;
  }
  const num = stringValue.replace(/\D/g, ''); // Remove tudo que não for número
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Adiciona ponto como separador de milhar
};

export const formatarPercentual = (value: string | number): string => {
  if (!value && value !== 0) return '';
  const stringValue = typeof value === 'string' ? value : value.toString();
  const num = stringValue.replace(/\D/g, ''); // Remove tudo que não for número
  if (!num) return '';
  return `${parseInt(num, 10)}%`;
};

// Função para remover strings vazias e convertê-las para undefined
export const sanitizarForm = (form: any) => {
  return Object.keys(form).reduce((formSanitizado: any, key: string) => {
    formSanitizado[key] = form[key] === '' || 0 ? undefined : form[key]; // Converte strings vazias para undefined
    return formSanitizado;
  }, {});
};

// Funções de verificação de Datas
export const isDataMaiorOuIgual = (dataInicial: string, dataFinal: string) => {
  if (!dataInicial || !dataFinal) return false; // Retorna falso se uma das datas for nula

  const dateStart = new Date(dataInicial);
  const dateEnd = new Date(dataFinal);

  return dateEnd >= dateStart;
};

export const isDataMaior = (dataInicial: string, dataFinal: string) => {
  if (!dataInicial || !dataFinal) return false; // Retorna falso se uma das datas for nula

  const dateStart = new Date(dataInicial);
  const dateEnd = new Date(dataFinal);

  return dateEnd > dateStart;
};

export const isDataFutura = (date: string) => {
  if (!date) return false; // Retorna falso se uma das datas for nula

  const checkingDate = new Date(date);
  const currentDate = new Date();

  //Considera apenas as informações DD-MM-YYYY da data
  currentDate.setHours(0, 0, 0, 0);

  return checkingDate > currentDate;
};

// Função para formatar a data
export function formatDt(dateString: string): string {
  if (!dateString) {
    return 'Data inválida';
  }

  const date = new Date(dateString);
  const dia = String(date.getUTCDate()).padStart(2, '0');
  const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
  const ano = date.getUTCFullYear();

  return `${dia}/${mes}/${ano}`;
}

// Função para formatar a data e hora
export function formatDtTm(dateString: string): string {
  if (!dateString) {
    return 'Data e hora inválidas';
  }

  const date = new Date(dateString);
  const dia = String(date.getUTCDate()).padStart(2, '0');
  const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
  const ano = date.getUTCFullYear();
  const horas = String(date.getUTCHours()).padStart(2, '0');
  const minutos = String(date.getUTCMinutes()).padStart(2, '0');

  return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
}

//Formatar Breadcrumb
export function formatBreadcrumbTitle(title: string) {
  const segments = title.split(' / '); // Divide o título em partes usando " / "

  if (segments.length > 3) {
    const highlightedSegment = segments[2]; // O terceiro segmento
    const before = segments.slice(0, 2).join(' / '); // Os dois primeiros segmentos
    const after = segments.slice(3).join(' / '); // Os segmentos após o terceiro

    return { before, highlightedSegment, after };
  }

  return {
    before: segments.slice(0, -1).join(' / '),
    highlightedSegment: segments.slice(-1)[0],
    after: '',
  }; // Se <= 3 segmentos
}

// Função para formatar valor numérico para dinheiro em Reais (R$)
export function formatValorDinheiro(valor: string, casas_decimais = 2) {
  let valorDinheiro = '';
  if (!valor || valor.length == 0) {
    return '';
  } else {
    if (valor.includes(','))
      valorDinheiro = valor.replace('.', '').replace(',', '.');
    else valorDinheiro = valor;
    let [valorInteiro, valorDecimal] = valorDinheiro.split('.');
    if (valorDecimal != undefined && valorDecimal.length < casas_decimais)
      valorDecimal =
        valorDecimal + '0'.repeat(casas_decimais - valorDecimal.length);
    if (valorDecimal == undefined) {
      valorDecimal = '0'.repeat(casas_decimais);
    }
    // else valorDecimal = '0'.repeat(casas_decimais);
    valorInteiro = formatarNumero(valorInteiro);

    return `${valorInteiro},${valorDecimal}`;
  }
}

export function formatoMoney(valor: string, casasDecimais = 2): string {
  // Remove qualquer caractere que não seja número
  let valorNumerico = valor.replace(/\D/g, '');

  // Se não houver valor, retorna '0' formatado corretamente
  if (!valorNumerico) return '0,' + '0'.repeat(casasDecimais);

  // Se o número for menor que as casas decimais, adiciona zeros à esquerda
  while (valorNumerico.length <= casasDecimais) {
    valorNumerico = '0' + valorNumerico;
  }

  // Divide parte inteira e decimal
  const valorInteiro = valorNumerico.slice(0, -casasDecimais);
  const valorDecimal = valorNumerico.slice(-casasDecimais);

  // Formata a parte inteira com separador de milhar
  const inteiroFormatado = parseInt(valorInteiro, 10).toLocaleString('pt-BR');

  return `${inteiroFormatado},${valorDecimal}`;
}

export function clearAllCache(...keys: string[]) {
  try {
    if (!keys || keys.length === 0) {
      console.warn(
        'Nenhuma chave de cache foi passada. Limpando todo o localStorage.',
      );
      localStorage.clear();
      console.log('Todos os dados do localStorage foram removidos.');
      return;
    }

    let removedAny = false;

    keys.forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`Cache removido: ${key}`);
        removedAny = true;
      } else {
        console.warn(`Chave não encontrada no cache: ${key}`);
      }
    });

    if (removedAny) {
      console.log('Status atualizado do localStorage:');
    } else {
      console.warn(
        'Nenhuma alteração feita. O cache já estava limpo ou as chaves não existiam.',
      );
    }
  } catch (error) {
    console.error('Erro ao limpar o cache:', error);
  }
}
export function clearAllCacheExcept(exceptKeys: string[] = ['token']) {
  try {
    const allKeys = Object.keys(localStorage);
    let removedAny = false;

    allKeys.forEach((key) => {
      if (!exceptKeys.includes(key)) {
        localStorage.removeItem(key);
        console.log(`Cache removido: ${key}`);
        removedAny = true;
      }
    });

    if (removedAny) {
      console.log('Cache limpo, exceto as chaves preservadas:', exceptKeys);
    } else {
      console.warn(
        'Nenhuma chave removida. Tudo já estava limpo ou somente as chaves preservadas estavam presentes.',
      );
    }
  } catch (error) {
    console.error('Erro ao limpar o cache:', error);
  }
}

export function transformarEnumOptionValueEmLabel(
  optionValue: number,
  enumOptions: EnumOption[],
): string {
  const enumOption = enumOptions.find(
    (option) => option.value === optionValue.toString(),
  );
  return enumOption?.label || 'Não encontrado';
}

export const calculateColumnWidth = (totalItems: number) => {
  // Arredonda para 2 casas decimais para evitar problemas de precisão
  const width = Number((100 / totalItems).toFixed(2));
  return `${width}%`;
};

export const formatarValorFormatoDinheiro = (valor: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor);
};

export const parseInputMonetario = (valor: string): number => {
  // Remove tudo que não for número
  const somenteNumeros = valor.replace(/\D/g, '');

  if (!somenteNumeros) return 0;

  // Divide por 100 para ter 2 casas decimais fixas
  return parseFloat((parseInt(somenteNumeros, 10) / 100).toFixed(2));
};

export const capitalize = (str: string) => {
  if (!str) return;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export async function getToken() {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Token não encontrado');
  }
  return token;
}
export function validarCpf(cpf: string): boolean {
  cpf = cpf.replace(/[^\d]+/g, '');
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  const digito1 = resto >= 10 ? 0 : resto;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  const digito2 = resto >= 10 ? 0 : resto;

  return (
    digito1 === parseInt(cpf.charAt(9)) && digito2 === parseInt(cpf.charAt(10))
  );
}

export function getDominioOptions(
  dominioPaiIdentifier: string | number,
): EnumOption[] {
  const cachedDominios = localStorage.getItem('dominios_select');
  if (!cachedDominios) {
    console.warn('Cache de domínios não encontrado no localStorage.');
    return [{ value: '0', label: 'Não informado' }];
  }

  try {
    const dominios: IDominioSelect[] = JSON.parse(cachedDominios);

    const filteredDominios = dominios.filter((item) => {
      if (typeof dominioPaiIdentifier === 'string') {
        // Se o identificador for uma string, assume-se que é o nome do pai
        return item.nomeDominioPai === dominioPaiIdentifier;
      }
      // Se o identificador for um número, assume-se que é o ID do pai
      return item.dominioPai === dominioPaiIdentifier;
    });

    const options = filteredDominios
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
      .map((item) => ({
        value: item.id.toString(),
        label: item.nome,
      }));

    return [{ value: '0', label: 'Não informado' }, ...options];
  } catch (error) {
    console.error(
      'Erro ao processar ou filtrar domínios do localStorage:',
      error,
    );
    return [{ value: '0', label: 'Não informado' }];
  }
}
export function isSuperAdmin(): boolean {
  const user = getUser();
  if (user && Array.isArray(user.vinculo)) {
    return user.vinculo.some((v: any) => v.superAdmin === true);
  }
  return false;
}
